import { debounce } from "lodash";
import { graphql } from "@repo/graphql-types/gql";
import { useRouteContext, useSearch, useNavigate } from "@tanstack/react-router";
import { AsyncDataWrapper } from "@/components/async-data-wrapper";
import { SearchInputWithMinLength } from "@/components/search-input-with-min-length";
import { useGraphQL } from "@/hooks/use-graphql";
import { CommunityFollowers } from "@/components/community-followers";
import { ButtonTabs } from "@/components/button-tabs";
import { CommunityListMap } from "@/components/community-list-map";

const CommunityListSearchQuery = graphql(/* GraphQL */ `
  query CommunityListSearchQuery(
    $nomeMembro: String! = ""
    $codEspecialidades: [Int!] = []
    $codUnidades: [Int!] = []
    $codUsuario: Int!
  ) {
    perfisComunidade(
      first: 10
      stringBusca: $nomeMembro
      codEspecialidades: $codEspecialidades
      codUnidades: $codUnidades
    ) {
      ...CommunityListMapFragment
    }
    seguindo: nexus_follow(
      where: { codUsuarioFollower: { _eq: $codUsuario } }
      order_by: { createdAt: desc }
    ) {
      codUsuarioFollowing
    }
  }
`);

export const CommunityList = (): JSX.Element => {
  const search = useSearch({ from: "/settings/community/" });
  const navigate = useNavigate();
  const { user, flags } = useRouteContext({
    strict: false,
  });

  const selectedTab = Number(search.tab ?? 0);
  const memberName = search.memberName ?? "";

  const debouncedSetSearch = debounce((newName: string) => {
    void navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        memberName: newName,
      }),
      replace: true,
    });
  }, 1000);

  const handleTabChange = (id: string): void => {
    const tabIndex = tabs.findIndex((tab) => tab.id === id);
    void navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        tab: tabIndex,
      }),
      replace: true,
    });
  };

  const showFavoriteTab = flags["mostra-aba-de-favoritos-na-comunidade-do-app"];

  const queryResult = useGraphQL(CommunityListSearchQuery, {
    nomeMembro: memberName,
    codEspecialidades: search.specialties?.map(Number) ?? [],
    codUnidades: (search.locations as string[] | undefined)?.map(Number) ?? [],
    codUsuario: user.codUsuario,
  });

  const { data } = queryResult;

  const followerIds =
    data?.seguindo && data.seguindo.length > 0
      ? data.seguindo.map((f) => f.codUsuarioFollowing).filter(Boolean)
      : [0];

  const tabs = [
    { id: "0", label: "Todos" },
    { id: "1", label: "Favoritos" },
  ];

  return (
    <div className="flex flex-col gap-4">
      <SearchInputWithMinLength
        onSearch={debouncedSetSearch}
        placeholder="Nome do profissional"
        title=""
      />
      {showFavoriteTab ? (
        <ButtonTabs
          activeTab={selectedTab.toString()}
          onTabChange={handleTabChange}
          tabs={tabs}
        />
      ) : null}

      {selectedTab === 1 ? (
        <CommunityFollowers
          codUsuarios={followerIds}
          tabSelectedLabel={tabs[selectedTab]?.label}
        />
      ) : null}

      {selectedTab === 0 ? (
        <AsyncDataWrapper {...queryResult}>
          {data?.perfisComunidade ? (
            <CommunityListMap
              listData={data.perfisComunidade}
              tabSelectedLabel={tabs[selectedTab]?.label}
            />
          ) : null}
        </AsyncDataWrapper>
      ) : null}
    </div>
  );
};
